import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon, FileTextIcon } from 'lucide-react'
import {
	Button,
	PanelHeader,
	Spinner,
} from '@rentflowdev/ui'
import { useGetDocumentQuery, useGetDocumentTemplateQuery } from '@/api'
import { DocumentSidebar } from '@/components/document-sidebar'
import { DocumentBlockPreview } from '@/components/document-block-preview'
import { canPreviewInBrowser } from '@/lib/document-preview'

export const Route = createFileRoute('/_app/documents/$documentId')({
	component: DocumentPage,
	// Documents are opened from a property, deal, rental or tenant card as well
	// as from the registry. `from` carries the exact address to return to —
	// including the card's tab — so "back" does not dump everyone in the
	// registry. A plain history.back() would not survive a reload or a shared
	// link, which is how these pages are usually reached in testing.
	validateSearch: (search: Record<string, unknown>): { from?: string } => ({
		// Accept only an in-app path: `from` is pushed onto history verbatim, so
		// an absolute or protocol-relative URL would send Back and the
		// after-delete redirect off the app entirely. A single leading slash and
		// no second one is exactly the local-path shape.
		from:
			typeof search.from === 'string' && /^\/(?!\/)/.test(search.from)
				? search.from
				: undefined,
	}),
})

function DocumentPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const router = useRouter()
	const { documentId } = Route.useParams()
	const { from } = Route.useSearch()

	// `from` is a full in-app href, so it goes through history rather than the
	// typed navigate() — the target route varies and carries its own search.
	const goBack = () => {
		if (from) router.history.push(from)
		else void navigate({ to: '/documents/registry' })
	}

	const { data: doc, isLoading } = useGetDocumentQuery(documentId)
	const { data: template } = useGetDocumentTemplateQuery(doc?.templateId ?? '', {
		skip: !doc?.templateId,
	})

	if (isLoading) {
		return (
			<div className="flex h-full items-center justify-center">
				<Spinner size="md" />
			</div>
		)
	}

	if (!doc) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-sm text-(--color-muted-foreground)">{t('documents.notFound')}</p>
			</div>
		)
	}

	const filledData = doc.filledDataJson
		? (JSON.parse(doc.filledDataJson) as Record<string, unknown>)
		: {}

	return (
		<div className="flex h-full flex-col overflow-hidden">
			<PanelHeader
				leading={
					<Button
						variant="ghost"
						size="sm"
						iconLeft={<ArrowLeftIcon className="size-4" />}
						onClick={goBack}
					>
						{t('common.back')}
					</Button>
				}
				title={
					<span className="truncate text-sm font-[500] text-(--color-foreground)">
						{doc.title}
					</span>
				}
			/>

			<div className="flex flex-1 gap-0 overflow-hidden">
				{/* Document preview */}
				<div className="relative flex-1 overflow-auto bg-white">
					{doc.fileUrl && canPreviewInBrowser(doc.fileUrl) ? (
						<iframe
							src={doc.fileUrl}
							title={doc.title}
							className="h-full w-full border-0"
						/>
					) : doc.fileUrl ? (
						/* A Word document cannot be framed — the browser saves it and
						   the tab stays blank, which read as "opening downloads the
						   file". Say so and hand over the file deliberately. */
						<div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
							<FileTextIcon className="size-10 text-(--color-muted-foreground)" />
							<p className="text-sm text-(--color-muted-foreground)">
								{t('documents.preview.unsupported')}
							</p>
							<Button variant="secondary" size="sm" onClick={() => window.open(doc.fileUrl, '_blank')}>
								{t('documents.actions.download')}
							</Button>
						</div>
					) : template?.blocks?.length ? (
						<DocumentBlockPreview
							blocks={template.blocks}
							filledData={filledData}
						/>
					) : (
						<div className="flex h-full flex-col items-center justify-center gap-3">
							<p className="text-sm text-(--color-muted-foreground)">
								{t('documents.preview.placeholder', { defaultValue: 'Шаблон не загружен' })}
							</p>
						</div>
					)}
				</div>

				{/* Right sidebar */}
				<div className="w-72 shrink-0 border-l border-(--color-border) bg-(--color-background)">
					<DocumentSidebar
						doc={doc}
						// The document is gone, but the card it belonged to is not — send
						// the user back there rather than to the registry.
						onDeleted={goBack}
					/>
				</div>
			</div>
		</div>
	)
}
